/**
 * Calculate the number of working days in a date range.
 *
 * @param startDate - Start of the date range (inclusive)
 * @param endDate - End of the date range (inclusive)
 * @param workingDays - Array of ISO weekday numbers to count as working days [1=Mon...7=Sun]
 * @param excludedDates - Array of dates to exclude in MM-dd format (holidays)
 * @returns Number of working days in the range
 */
export function calculateWorkingDays(
  startDate: Date,
  endDate: Date,
  workingDays: number[],
  excludedDates: string[]
): number {
  let count = 0;

  // Iterate in UTC to match the UTC-constructed date bounds produced by
  // getDateRangeBounds/getFullPeriodEnd. Using local-time accessors here caused
  // an off-by-one (e.g. a UTC midnight start snapping back a day in negative-offset
  // timezones), inflating the day count for calendar-month ranges.
  const current = new Date(startDate);
  current.setUTCHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setUTCHours(23, 59, 59, 999);

  while (current <= end) {
    // Convert JS getUTCDay() (0=Sun) to ISO weekday (1=Mon...7=Sun)
    const jsDay = current.getUTCDay();
    const isoWeekday = jsDay === 0 ? 7 : jsDay;

    // Check if this day of week is a working day
    if (workingDays.includes(isoWeekday)) {
      // Check if this date is NOT a holiday
      const monthDay = formatMonthDay(current);
      if (!excludedDates.includes(monthDay)) {
        count++;
      }
    }

    // Move to next day
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return count;
}

/**
 * Format a date as MM-dd string for comparison with excludedDates
 */
function formatMonthDay(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${month}-${day}`;
}
